const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024;

const SUPPORTED_ATTACHMENT_EXTENSIONS = [
  '.kml',
  '.xlsx',
  '.xls',
  '.csv',
  '.json',
  '.pdf',
  '.txt',
  '.doc',
  '.docx',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
];

export const BUG_REPORT_ATTACHMENT_CONFIG = {
  maxAttachmentSizeBytes: MAX_ATTACHMENT_SIZE_BYTES,
  supportedAttachmentExtensions: SUPPORTED_ATTACHMENT_EXTENSIONS,
} as const;

/**
 * Normalize backend `max_upload_file_bytes` to a safe positive integer, or use fallback.
 */
export const coerceServerMaxUploadBytes = (value: unknown, fallback: number): number => {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return fallback;
  }
  return Math.floor(value);
};

/**
 * Human-readable limit for UI copy and validation errors (e.g. "10MB", "1.5MB", "512KB").
 */
export const formatAttachmentMaxLabel = (maxBytes: number): string => {
  if (!Number.isFinite(maxBytes) || maxBytes <= 0) {
    return `${Math.round(MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024))}MB`;
  }
  const mb = maxBytes / (1024 * 1024);
  if (mb >= 1) {
    const roundedTenth = Math.round(mb * 10) / 10;
    const display = Number.isInteger(roundedTenth) ? String(roundedTenth) : roundedTenth.toFixed(1);
    return `${display}MB`;
  }
  const kb = Math.max(1, Math.round(maxBytes / 1024));
  return `${kb}KB`;
};

export const normalizeAttachmentExtensions = (extensions: string[]): string[] => {
  const unique = new Set<string>();
  extensions.forEach((extension) => {
    if (!extension) {
      return;
    }
    const normalized = extension.startsWith('.') ? extension.toLowerCase() : `.${extension.toLowerCase()}`;
    unique.add(normalized);
  });
  return Array.from(unique).sort();
};

export const validateBugReportAttachment = (
  file: File,
  supportedExtensions: string[] = BUG_REPORT_ATTACHMENT_CONFIG.supportedAttachmentExtensions as unknown as string[],
  maxAttachmentSizeBytes: number = BUG_REPORT_ATTACHMENT_CONFIG.maxAttachmentSizeBytes
): string | null => {
  const extension = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
  if (!supportedExtensions.includes(extension)) {
    return `Unsupported file type: ${extension || 'unknown'}`;
  }

  if (file.size > maxAttachmentSizeBytes) {
    return `File exceeds ${formatAttachmentMaxLabel(maxAttachmentSizeBytes)}: ${file.name}`;
  }

  return null;
};
